// Third-party
import useTranslation from 'next-translate/useTranslation';

import { useAtomValue } from 'jotai';

// Hooks
import { useFindSPFromMessageId } from '@/components/pages/conversations/SocialInbox/hooks/useFindSPFromMessage';
import { useSocialInboxDataSource } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxDataSource';
import { useSocialInboxNavigation } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxNavigation';
import useComments from '@/utils/hooks/swr/useComments';
import useReplies from '@/utils/hooks/swr/useReplies';

import { MessageType } from '@/types/messageType';

import { handleMentionReply, TAG } from '@/lib/manage/mentions';
import { COMMENT_REPLY, MENTION_REPLY } from '@/lib/routes/apiEndpoints';
// Types/Lib
import { SOCIAL_INBOX_META } from '@/components/pages/conversations/SocialInbox/const';

// Atoms
import { openedMessageAtom } from '@/components/pages/conversations/SocialInbox/atoms';

// Components
import { CommentForm } from '@/components/composite/Comments/CommentForm';

interface SocialInboxFormProps {
    onSubmit?: () => void;
}

export const SocialInboxForm = ({ onSubmit }: SocialInboxFormProps) => {
    const { t } = useTranslation('conversations');
    const { currentSocialInbox } = useSocialInboxNavigation();
    const openedMessage = useAtomValue(openedMessageAtom) as MessageType;
    // TODO: fix type when refactoring useComments and useReplies to use TS
    const { dataComments, mutateComments } = useComments({
        activeSPID: openedMessage?.social_profile_id,
        mediaId: openedMessage?.media_id
    } as any);
    const { mutateReplies } = useReplies({
        activeSPID: openedMessage?.social_profile_id,
        mediaId: openedMessage?.media_id,
        commentId: openedMessage?.id
    } as any);
    const { mutateMessages } = useSocialInboxDataSource();
    const { socialProfile } = useFindSPFromMessageId({ message: openedMessage });

    const updateReplies = (comment: MessageType) => {
        mutateReplies(
            (prevState) =>
                prevState?.map((page, index) =>
                    index === 0 ? { ...page, data: [comment, ...page.data] } : page
                ),
            false
        );
    };

    const updateComments = () => {
        const getUpdatedComment = (message: MessageType) =>
            message.id === openedMessage.id
                ? { ...message, replies_count: message.replies_count + 1 }
                : message;

        mutateComments(
            (prevState) =>
                prevState.map((page) => {
                    // Update comment data
                    const updatedData = page.data.map(getUpdatedComment);

                    // Update random picker winners if they exist
                    const updatedWinners = Array.isArray(page?.random_picker_winners)
                        ? page.random_picker_winners.map(getUpdatedComment)
                        : page?.random_picker_winners;

                    return {
                        ...page,
                        data: updatedData,
                        random_picker_winners: updatedWinners,
                        summary: {
                            ...page.summary,
                            total_count: page.summary.total_count + 1
                        }
                    };
                }),
            false
        );
    };

    const handleSubmit = (comment: MessageType) => {
        if (currentSocialInbox === SOCIAL_INBOX_META.mention.key) {
            handleMentionReply(openedMessage.id, comment, mutateMessages);
        } else {
            updateReplies(comment);
            updateComments();
        }

        onSubmit?.();
    };

    const getSubmitOptions = () => {
        if (currentSocialInbox === SOCIAL_INBOX_META.mention.key) {
            return {
                endpoint: MENTION_REPLY,
                params: { mentionID: openedMessage.id }
            };
        }
        return {
            endpoint: COMMENT_REPLY,
            params: {
                mediaID: openedMessage.media_id,
                commentID: openedMessage.id
            }
        };
    };

    if (!openedMessage) {
        return null;
    }

    return (
        <div className="p-4 pt-0 sm:p-10 sm:pt-0">
            <CommentForm
                placeholder={t('social_inbox.input.placeholder')}
                socialProfile={socialProfile}
                onSubmit={handleSubmit}
                // Display the mention options
                comments={currentSocialInbox === SOCIAL_INBOX_META.mention.key ? [] : dataComments}
                variant="socialInbox"
                submitOptions={getSubmitOptions()}
                // Reply is not allowed for tag mentions
                disabled={'mentioned_in' in openedMessage && openedMessage?.mentioned_in === TAG}
            />
        </div>
    );
};
